// assets/product-custom-property.js
import { Component } from "@theme/component";

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} textInput - The text input.
 * @property {HTMLElement} characterCount - The character count element.
 * @property {HTMLInputElement} [toggleInput] - The engraving toggle input.
 * @property {HTMLElement} [previewText] - The engraving preview output element.
 * @property {HTMLElement} [cardBody] - The collapsible engraving card body.
 * @property {HTMLElement} [collapsedMsg] - The message displayed when collapsed.
 */

/**
 * A custom element that manages product custom properties
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  connectedCallback() {
    super.connectedCallback?.();
    this.#bindEngravingEvents();
    this.#updateCharacterCount();
    this.#updateLivePreview();
    this.#updateToggleState();
  }

  handleInput() {
    this.#updateCharacterCount();
    this.#updateLivePreview();
  }

  handleToggle() {
    this.#updateToggleState();
  }

  #bindEngravingEvents() {
    const toggle = this.querySelector("[data-engraving-toggle]");
    if (toggle) {
      toggle.addEventListener("change", () => this.handleToggle());
    }

    const input = this.querySelector("input.engraving-input, input.field__input");
    if (input) {
      input.addEventListener("input", () => this.handleInput());

      const form = input.form;
      if (form) {
        form.addEventListener("submit", () => {
          const toggleEl = this.querySelector("[data-engraving-toggle]");
          if ((toggleEl && !toggleEl.checked) || !input.value.trim()) {
            input.disabled = true;
            setTimeout(() => {
              input.disabled = false;
            }, 1500);
          }
        });
      }
    }
  }

  #updateCharacterCount() {
    const characterCount = this.querySelector("[ref=\"characterCount\"]") || this.refs?.characterCount;
    const textInput = this.querySelector("[ref=\"textInput\"]") || this.refs?.textInput;
    if (!characterCount || !textInput) return;

    const currentLength = textInput.value.length;
    const maxLength = textInput.maxLength || 25;

    const template = characterCount.getAttribute("data-template") || "[current] / [max] Zeichen";
    const updatedText = template.replace("[current]", currentLength.toString()).replace("[max]", maxLength.toString());

    characterCount.textContent = updatedText;
  }

  #updateLivePreview() {
    const previewText = this.querySelector("[ref=\"previewText\"]") || this.refs?.previewText;
    const textInput = this.querySelector("[ref=\"textInput\"]") || this.refs?.textInput;
    if (!previewText) return;

    const val = textInput?.value?.trim();
    if (val && val.length > 0) {
      previewText.textContent = val;
      previewText.classList.add("has-text");
    } else {
      const placeholder = previewText.getAttribute("data-placeholder") || "Leo 2026";
      previewText.textContent = placeholder;
      previewText.classList.remove("has-text");
    }
  }

  #updateToggleState() {
    const toggleInput = this.querySelector("[data-engraving-toggle]") || this.refs?.toggleInput;
    const cardBody = this.querySelector("[ref=\"cardBody\"]") || this.refs?.cardBody;
    const textInput = this.querySelector("[ref=\"textInput\"]") || this.refs?.textInput;
    const collapsedMsg = this.querySelector("[ref=\"collapsedMsg\"]") || this.refs?.collapsedMsg;

    if (!toggleInput || !cardBody) return;

    const isChecked = toggleInput.checked;
    if (isChecked) {
      cardBody.classList.remove("is-collapsed");
      if (collapsedMsg) collapsedMsg.style.display = "none";
      if (textInput) {
        textInput.disabled = false;
      }
    } else {
      cardBody.classList.add("is-collapsed");
      if (collapsedMsg) collapsedMsg.style.display = "flex";
      if (textInput) {
        textInput.disabled = true;
      }
    }
  }
}

customElements.define("product-custom-property-component", ProductCustomProperty);
